"use client";

import { useDeferredValue, useEffect, useMemo, useState } from "react";
import {
  ChartStyleFields,
  useChartStyle,
} from "@/components/tool-kit/charts/controls";
import { ChartFigure, toTsv } from "@/components/tool-kit/charts/figure";
import { WordCloudSvg } from "@/components/tool-kit/diagrams/word-cloud";
import {
  Checkbox,
  Notice,
  Panel,
  PillGroup,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY, HINT } from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";
import {
  CHART_HEIGHT,
  CHART_PADDING,
  CHART_WIDTH,
  chartFilename,
  layoutTitle,
} from "@/lib/tool-kit/charts/layout";
import { CHART_FONT } from "@/lib/tool-kit/charts/text";
import {
  type CloudPalette,
  cloudColors,
  layoutCloud,
  type MeasureWord,
  parseStopwords,
  type SizeScale,
  selectWords,
  type WordMetrics,
  wordFrequencies,
  wordOrientations,
  wordSize,
} from "@/lib/tool-kit/diagrams/word-cloud";
import { downloadText } from "@/lib/tool-kit/download";
import { toCsv } from "@/lib/tool-kit/research-design/matrix";

/** English strings. A translated content file overrides these through `ui`. */
export const WORD_CLOUD_GENERATOR_STRINGS = {
  textLabel: "Your text",
  textHint:
    "Paste an essay, a speech, open-ended survey answers or interview notes. The words are counted in your browser — nothing is uploaded.",
  exampleText:
    "Four score and seven years ago our fathers brought forth on this continent, a new nation, conceived in Liberty, and dedicated to the proposition that all men are created equal.\n\nNow we are engaged in a great civil war, testing whether that nation, or any nation so conceived and so dedicated, can long endure. We are met on a great battle-field of that war. We have come to dedicate a portion of that field, as a final resting place for those who here gave their lives that that nation might live. It is altogether fitting and proper that we should do this.\n\nBut, in a larger sense, we can not dedicate—we can not consecrate—we can not hallow—this ground. The brave men, living and dead, who struggled here, have consecrated it, far above our poor power to add or detract. The world will little note, nor long remember what we say here, but it can never forget what they did here. It is for us the living, rather, to be dedicated here to the unfinished work which they who fought here have thus far so nobly advanced. It is rather for us to be here dedicated to the great task remaining before us—that from these honored dead we take increased devotion to that cause for which they gave the last full measure of devotion—that we here highly resolve that these dead shall not have died in vain—that this nation, under God, shall have a new birth of freedom—and that government of the people, by the people, for the people, shall not perish from the earth.",
  exampleTitle: "Most Frequent Words in the Gettysburg Address",
  tokens: "one:{count} word in the text|other:{count} words in the text",
  counted: "{count} counted",
  distinct: "one:{count} different word|other:{count} different words",
  shown: "{count} in the cloud",
  countingTitle: "What to count",
  removeStopwords: "Leave out stopwords (common words such as “the” and “of”)",
  stopwordsLabel: "Stopwords",
  stopwordsHint:
    "Separate words with spaces, commas or new lines. Add any word that dominates your text without saying much — often the topic itself.",
  stopwords:
    "a, about, above, after, again, against, all, also, am, among, an, and, any, are, as, at, be, because, been, before, being, below, between, both, but, by, can, cannot, could, did, do, does, doing, down, during, each, either, even, ever, every, few, for, from, further, had, has, have, having, he, her, here, hers, herself, him, himself, his, how, however, i, if, in, into, is, it, its, itself, just, less, may, me, might, more, most, much, must, my, myself, neither, no, nor, not, now, of, off, often, on, once, one, only, or, other, others, our, ours, ourselves, out, over, own, per, rather, same, shall, she, should, since, so, some, such, than, that, the, their, theirs, them, themselves, then, there, therefore, these, they, this, those, though, through, thus, to, too, under, until, up, upon, us, very, via, was, we, were, what, when, where, whether, which, while, who, whom, whose, why, will, with, within, without, would, yet, you, your, yours, yourself, don't, doesn't, didn't, isn't, aren't, wasn't, weren't, can't, couldn't, wouldn't, shouldn't, won't, that's, there's, they're, you're, i've, i'd, i'll, i'm",
  foldCase: "Treat capitals as the same word (Nation and nation)",
  includeNumbers: "Count numbers, such as years, as words",
  minCountLabel: "Smallest count to include",
  maxWordsLabel: "Most words in the cloud",
  lookTitle: "How it looks",
  orientationLabel: "Word direction",
  horizontal: "All horizontal",
  mixed: "Some vertical",
  scaleLabel: "Word size",
  scaleSqrt: "Area follows the count (square root)",
  scaleLinear: "Height follows the count (linear)",
  paletteLabel: "Colors",
  paletteColorblind: "Color-blind safe (Okabe–Ito)",
  paletteBlue: "Shades of blue",
  paletteGray: "Black and gray, for print",
  newLayout: "New layout",
  layoutNumber: "Layout {number}",
  measuring: "Arranging the words…",
  emptyText: "Paste some text to make a word cloud.",
  noWords:
    "No words are left to draw. Lower the smallest count, or shorten the stopword list.",
  dropped:
    "one:{count} word did not fit and was left out of the cloud — it is still in the table.|other:{count} words did not fit and were left out of the cloud — they are still in the table.",
  rankColumn: "Rank",
  wordColumn: "Word",
  countColumn: "Count",
  downloadCsv: "Download frequency table (CSV)",
  labelsAndStyle: "Title and figure style",
  chartTitle: "Title",
  apaStyle: "APA style figure",
  apaHint:
    "Takes the title out of the image and sets the figure number in bold and the title in italics above it — the APA 7 layout. Say in a note what was counted and what was left out.",
  figureNumber: "Figure number",
  figureNote: "Note (optional)",
  figureLabel: "Figure {number}",
  noteLabel: "Note.",
  copyCaption: "Copy number and title",
  downloadSvg: "Download SVG",
  downloadPng: "Download PNG",
  copyData: "Copy frequencies",
  copied: "Copied!",
  exportFailed:
    "This browser could not create the PNG. Try Download SVG, or another browser.",
  showDataTable: "Show every counted word as a table",
  chartLabel: "Word cloud",
  chartDescription:
    "Word cloud of the {count} most frequent words. The largest are {top}.",
  topWord: "{word} ({count})",
} as const;

type Strings = typeof WORD_CLOUD_GENERATOR_STRINGS;
type Orientation = "horizontal" | "mixed";

const MAX_SIZE = 64;
const MIN_SIZE = 12;

/** A measurer backed by a canvas, which knows the browser's real font metrics. */
function canvasMeasure(): MeasureWord | null {
  const context = document.createElement("canvas").getContext("2d");
  if (!context) {
    return null;
  }
  context.font = `bold 100px ${CHART_FONT}`;
  const cache = new Map<string, WordMetrics>();
  return (text, size) => {
    let base = cache.get(text);
    if (!base) {
      const metrics = context.measureText(text);
      base = {
        width: metrics.width,
        ascent: metrics.actualBoundingBoxAscent || 72,
        descent: metrics.actualBoundingBoxDescent || 21,
      };
      cache.set(text, base);
    }
    const scale = size / 100;
    return {
      width: base.width * scale,
      ascent: base.ascent * scale,
      descent: base.descent * scale,
    };
  };
}

function statsLine(
  stats: { tokens: number; counted: number; distinct: number; shown: number },
  strings: Strings,
  locale: string,
  fmt: (value: number) => string
): string {
  return [
    tPlural(strings.tokens, locale, stats.tokens, { count: fmt(stats.tokens) }),
    t(strings.counted, { count: fmt(stats.counted) }),
    tPlural(strings.distinct, locale, stats.distinct, {
      count: fmt(stats.distinct),
    }),
    t(strings.shown, { count: fmt(stats.shown) }),
  ].join(" · ");
}

export default function WordCloudGeneratorWidget() {
  const strings = useToolUi(WORD_CLOUD_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [raw, setRaw] = useState<string>(strings.exampleText);
  const [stopText, setStopText] = useState<string>(strings.stopwords);
  const [removeStop, setRemoveStop] = useState(true);
  const [foldCase, setFoldCase] = useState(true);
  const [numbers, setNumbers] = useState(false);
  const [minCountRaw, setMinCountRaw] = useState("1");
  const [maxWordsRaw, setMaxWordsRaw] = useState("60");
  const [orientation, setOrientation] = useState<Orientation>("horizontal");
  const [scale, setScale] = useState<SizeScale>("sqrt");
  const [palette, setPalette] = useState<CloudPalette>("colorblind");
  const [seed, setSeed] = useState(1);
  const [measure, setMeasure] = useState<MeasureWord | null>(null);
  const [style, update] = useChartStyle({
    title: strings.exampleTitle,
    xTitle: "",
    yTitle: "",
    color: "blue",
    valueLabels: false,
    apa: false,
    figureNumber: "1",
    note: "",
  });

  // Text is measured with a canvas, which exists only in the browser.
  useEffect(() => {
    setMeasure(() => canvasMeasure());
  }, []);

  const text = useDeferredValue(raw);
  const frequencies = useMemo(
    () =>
      wordFrequencies(text, {
        stopwords: parseStopwords(stopText, locale),
        removeStopwords: removeStop,
        foldCase,
        includeNumbers: numbers,
        locale,
      }),
    [text, stopText, removeStop, foldCase, numbers, locale]
  );
  const minCount = Math.max(1, parseIntegerInput(minCountRaw) ?? 1);
  const maxWords = Math.min(
    200,
    Math.max(1, parseIntegerInput(maxWordsRaw) ?? 60)
  );
  const words = useMemo(
    () => selectWords(frequencies.words, minCount, maxWords),
    [frequencies, minCount, maxWords]
  );
  const title = style.apa ? null : layoutTitle(style.title, CHART_WIDTH);
  const top = CHART_PADDING + (title?.height ?? 0);
  const cloud = useMemo(() => {
    if (!measure || words.length === 0) {
      return null;
    }
    const most = words[0].count;
    const vertical = wordOrientations(
      words.length,
      orientation === "mixed",
      seed
    );
    return layoutCloud(
      words.map((word, index) => ({
        text: word.text,
        size: wordSize(word.count, most, scale, MAX_SIZE, MIN_SIZE),
        vertical: vertical[index],
      })),
      {
        x: CHART_PADDING,
        y: top,
        width: CHART_WIDTH - 2 * CHART_PADDING,
        height: CHART_HEIGHT - top - CHART_PADDING,
      },
      measure,
      { seed }
    );
  }, [measure, words, orientation, scale, seed, top]);
  const colors = cloudColors(words.length, palette);
  const head = [strings.rankColumn, strings.wordColumn, strings.countColumn];
  const rows = frequencies.words.map((word, index) => [
    fmt(index + 1),
    word.text,
    fmt(word.count),
  ]);
  const raws = frequencies.words.map((word) => [word.text, word.count]);
  const filename = chartFilename(style.title, "word-cloud");
  const description = t(strings.chartDescription, {
    count: fmt(words.length),
    top: words
      .slice(0, 5)
      .map((word) =>
        t(strings.topWord, { word: word.text, count: fmt(word.count) })
      )
      .join(", "),
  });

  return (
    <div className="space-y-6">
      <TextAreaField
        hint={strings.textHint}
        id="wcg-text"
        label={strings.textLabel}
        onChange={setRaw}
        prose
        rows={8}
        value={raw}
      />
      <div aria-live="polite" className="space-y-2">
        {frequencies.tokens > 0 ? (
          <p className={HINT}>
            {statsLine(
              {
                tokens: frequencies.tokens,
                counted: frequencies.counted,
                distinct: frequencies.words.length,
                shown: words.length,
              },
              strings,
              locale,
              fmt
            )}
          </p>
        ) : (
          <Notice>{strings.emptyText}</Notice>
        )}
        {frequencies.tokens > 0 && words.length === 0 ? (
          <Notice>{strings.noWords}</Notice>
        ) : null}
        {cloud && cloud.dropped.length > 0 ? (
          <Notice>
            {tPlural(strings.dropped, locale, cloud.dropped.length, {
              count: fmt(cloud.dropped.length),
            })}
          </Notice>
        ) : null}
      </div>
      {words.length > 0 && cloud ? (
        <ChartFigure
          apa={style.apa}
          data={toTsv([[strings.wordColumn, strings.countColumn], ...raws])}
          figureNumber={style.figureNumber}
          filename={filename}
          labels={strings}
          note={style.note}
          table={{ head, rows }}
          title={style.title}
        >
          <WordCloudSvg
            colors={colors}
            description={description}
            height={CHART_HEIGHT}
            id="wcg-cloud"
            label={style.title.trim() || strings.chartLabel}
            title={title}
            width={CHART_WIDTH}
            words={cloud.words}
          />
        </ChartFigure>
      ) : null}
      {words.length > 0 && !cloud ? (
        <div className="flex aspect-[8/5] items-center justify-center rounded-lg border border-border bg-white text-muted-foreground text-sm">
          {strings.measuring}
        </div>
      ) : null}
      <div className="flex flex-wrap items-center gap-3">
        <button
          className={BUTTON_SECONDARY}
          disabled={words.length === 0}
          onClick={() => setSeed((value) => value + 1)}
          type="button"
        >
          {strings.newLayout}
        </button>
        <span className={HINT}>
          {t(strings.layoutNumber, { number: fmt(seed) })}
        </span>
        <button
          className={BUTTON_SECONDARY}
          disabled={raws.length === 0}
          onClick={() =>
            downloadText(
              `${filename}-frequencies.csv`,
              toCsv([[strings.wordColumn, strings.countColumn], ...raws]),
              "text/csv;charset=utf-8"
            )
          }
          type="button"
        >
          {strings.downloadCsv}
        </button>
      </div>
      <Panel title={strings.countingTitle}>
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="wcg-min"
            label={strings.minCountLabel}
            numeric
            onChange={setMinCountRaw}
            value={minCountRaw}
          />
          <TextField
            id="wcg-max"
            label={strings.maxWordsLabel}
            numeric
            onChange={setMaxWordsRaw}
            value={maxWordsRaw}
          />
        </div>
        <Checkbox
          checked={foldCase}
          label={strings.foldCase}
          onChange={setFoldCase}
        />
        <Checkbox
          checked={numbers}
          label={strings.includeNumbers}
          onChange={setNumbers}
        />
        <Checkbox
          checked={removeStop}
          label={strings.removeStopwords}
          onChange={setRemoveStop}
        />
        {removeStop ? (
          <TextAreaField
            hint={strings.stopwordsHint}
            id="wcg-stopwords"
            label={strings.stopwordsLabel}
            onChange={setStopText}
            prose
            value={stopText}
          />
        ) : null}
      </Panel>
      <Panel title={strings.lookTitle}>
        <div className="grid gap-4 sm:grid-cols-2">
          <PillGroup
            label={strings.orientationLabel}
            name="wcg-orientation"
            onChange={setOrientation}
            options={[
              { value: "horizontal", label: strings.horizontal },
              { value: "mixed", label: strings.mixed },
            ]}
            value={orientation}
          />
          <SelectField
            id="wcg-palette"
            label={strings.paletteLabel}
            onChange={setPalette}
            options={[
              { value: "colorblind", label: strings.paletteColorblind },
              { value: "blue", label: strings.paletteBlue },
              { value: "gray", label: strings.paletteGray },
            ]}
            value={palette}
          />
        </div>
        <PillGroup
          label={strings.scaleLabel}
          name="wcg-scale"
          onChange={setScale}
          options={[
            { value: "sqrt", label: strings.scaleSqrt },
            { value: "linear", label: strings.scaleLinear },
          ]}
          value={scale}
        />
      </Panel>
      <ChartStyleFields
        idPrefix="wcg"
        labels={strings}
        style={style}
        update={update}
      />
    </div>
  );
}
